import React, { useState } from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from '../../../../../components/common/Modal'
import { ModalHeader } from '../../../../../components/common/Modal/ModalHeader'
import { ModalBody } from '../../../../../components/common/Modal/ModalBody'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../../components/common/Toast/toast-type'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { EditSocietyWebsiteModalContent } from './EditSocietyWebsiteModalContent'
import { websiteQueries } from '../../../../../api/society-websites'
import { ISocietyWebsiteModel } from '../../../../../interfaces/models/societies-websites.interfaces'
import { adminEditSocietyWebsiteSchema } from '../../../../../forms/schemas/admin_edit_society_website'
import { Spinner } from '../../../../../components/common/Spinner'
import { patchSocietyWebsite } from '../../../../../api/admin'

interface EditSocietyWebsiteModalProps {
  show: boolean
  close: () => void
  societyId?: string
}

export const EditSocietyWebsiteModal = ({
  show,
  close,
  societyId,
}: EditSocietyWebsiteModalProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const [updatingWebsite, setUpdatingWebsite] = useState(false)
  const { data: website } = useQuery(
    websiteQueries.getWebsitePerSociety(societyId)
  )

  const { mutate: mutatePatchWebsite } = useMutation({
    mutationFn: patchSocietyWebsite,
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('Föreningshemsida uppdaterad')
      )
      close()
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
    },
  })

  const onSubmit = async (
    data: InferType<typeof adminEditSocietyWebsiteSchema>
  ): Promise<void> => {
    setUpdatingWebsite(true)
    const dataTosend: Partial<ISocietyWebsiteModel> = { ...data }
    // Backend doesn't support setting slug to existing value
    if (data.slug === website?.slug) {
      delete dataTosend.slug
    }
    societyId &&
      mutatePatchWebsite({
        societyId,
        data: dataTosend,
      })
  }

  const onError = (error: unknown): void => {
    captureException(error)
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  return (
    <Modal show={show} size="xl">
      {{
        header: (
          <ModalHeader onClose={close}>Uppdatera föreningshemsida</ModalHeader>
        ),
        body: (
          <ModalBody className="overflow-y-auto">
            {updatingWebsite || !website ? (
              <Spinner />
            ) : (
              <EditSocietyWebsiteModalContent
                onSubmit={onSubmit}
                onError={onError}
                onClose={close}
                website={website}
              />
            )}
          </ModalBody>
        ),
      }}
    </Modal>
  )
}
